import React from 'react';
import { Users, UserCheck, UserX, Shield } from 'lucide-react';
import { StatCard, StatsGrid } from '../common/DashboardComponents';

/**
 * User Statistics Component
 * Displays user-related statistics in a grid
 */
export function UserStatistics({ statistics }) {
  if (!statistics) return null;

  const {
    total_users = 0,
    active_users = 0,
    disabled_users = 0,
    users_by_role = {},
    recent_signups = 0,
    recent_logins = 0
  } = statistics;

  return (
    <StatsGrid columns={4}>
      <StatCard
        title="Total Users"
        value={total_users}
        subtitle="All registered users"
        icon={Users}
        color="blue"
      />
      
      <StatCard
        title="Active Users"
        value={active_users}
        subtitle="Currently active"
        icon={UserCheck}
        color="green"
      />
      
      <StatCard
        title="Disabled Users"
        value={disabled_users}
        subtitle="Temporarily disabled"
        icon={UserX}
        color="red"
      />
      
      <StatCard
        title="Recent Logins"
        value={recent_logins}
        subtitle="Last 7 days"
        icon={Shield}
        color="purple"
      />
    </StatsGrid>
  );
}

/**
 * User Roles Breakdown Component
 */
export function UserRolesBreakdown({ usersByRole }) {
  if (!usersByRole || Object.keys(usersByRole).length === 0) {
    return null;
  }

  const roles = Object.entries(usersByRole).map(([role, count]) => ({
    role,
    count,
    percentage: 0
  }));

  const totalUsers = roles.reduce((sum, { count }) => sum + count, 0);
  
  roles.forEach(item => {
    item.percentage = totalUsers > 0 ? ((item.count / totalUsers) * 100).toFixed(1) : 0;
  });

  const roleColors = {
    'SUPER_ADMIN': 'bg-purple-500',
    'BUSINESS_ADMIN': 'bg-blue-500',
    'OPERATOR': 'bg-green-500',
    'VIEWER': 'bg-gray-500'
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Users by Role</h3>
      <div className="space-y-4">
        {roles.map(({ role, count, percentage }) => (
          <div key={role}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                {role.replace('_', ' ')}
              </span>
              <span className="text-sm text-gray-600">
                {count} ({percentage}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${roleColors[role] || 'bg-gray-500'}`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default UserStatistics;
